import React, {useEffect, useState} from 'react';
import {AssetCodeTypes} from "../../../components/menus/constants";
import TextViewFileUpdater from "../../../components/files/TextViewFileUpdater";
import CodeEditorPanel from "../fieldMng/shared/CodeEditorPanel";
import CodeOptionTreeEditor from "../fieldMng/shared/CodeOptionTreeEditor";
import PanelTextNav from "../fieldMng/shared/PanelTextNav";
import FieldSet from "../fieldMng/field/subPage/FieldSet";
import GroupCodeApprovalFields from "./GroupCodeApprovalFields";
import {GROUP_CODE_CHILD_LABELS, GROUP_CODE_EDITOR_LABELS} from "./groupCodeLabels";
import {GroupCodeAssetReadExtras} from "./GroupCodeCurrentCodeReadExtras";
import "../fieldMng/shared/groupFieldEditor.css";
import "./groupCodeAssetEditor.css";

const TABLE_NAME_MAP = {
    "자산그룹명": "group",
    "업무그룹명": "task",
    "위치명": "location",
    "SLA 그룹명": "group",
};

const ASSET_GROUP_RIGHT_PANEL_NAV = [
    {id: "fieldSettings", label: "항목 설정"},
    {id: "childCode", label: "하위코드 설정"},
];

function GroupCodeAssetEditor({
    assetCodeType,
    nameLabel: nameLabelProp,
    supportsApprovalLines = false,
    supportsApprovalBeforeOnly = false,
    supportsChildCodes = true,
    code,
    setCode,
    onUpdate,
    onDelete,
    onCreateChild,
    onUpdateChild,
    onDeleteChild,
    groupFields,
    setFieldFlag,
}) {
    const [rightPanelView, setRightPanelView] = useState("fieldSettings");
    const nameLabel = nameLabelProp ?? assetCodeType?.title ?? "코드명";
    const showBatchNumber = assetCodeType?.value === AssetCodeTypes.ASSET_CODE_GROUP.value;
    const showFile = assetCodeType?.value === AssetCodeTypes.ASSET_LOCATION_CODE.value;
    const showApprovalLines = supportsApprovalLines || supportsApprovalBeforeOnly;
    const showApprovalAfter = supportsApprovalLines && !supportsApprovalBeforeOnly;
    const tableName = TABLE_NAME_MAP[nameLabel] ?? "code";

    const extraContent = (
        <>
            {showApprovalLines && (
                <GroupCodeApprovalFields code={code} showAfter={showApprovalAfter} />
            )}
            {showBatchNumber && (
                <div className="code-editor__field">
                    <label>배치번호(자산코드)</label>
                    <input
                        className="basic-input input-style"
                        value={code.batchNumber ?? ""}
                        onChange={(e) => setCode((prev) => ({...prev, batchNumber: e.target.value}))}
                    />
                </div>
            )}

            {showFile && (
                <div className="code-editor__field">
                    <TextViewFileUpdater
                        nativeInput
                        inline
                        uploadLabel="도면업로드"
                        tableInfo={{tableName, joinKey: code.id}}
                        isSingle
                        onlyImage
                    />
                </div>
            )}
        </>
    );

    const readExtraContent = (
        <GroupCodeAssetReadExtras
            code={code}
            showApprovalLines={showApprovalLines}
            showApprovalAfter={showApprovalAfter}
            showBatchNumber={showBatchNumber}
            showFile={showFile}
            tableName={tableName}
        />
    );

    const editorPanel = (
        <CodeEditorPanel
            category="groupCode"
            item={code}
            setItem={setCode}
            onSave={onUpdate}
            onDelete={onDelete}
            nameLabel={nameLabel}
            saveLabel="저장"
            sectionTitle={GROUP_CODE_EDITOR_LABELS.sectionTitle}
            deleteTitle={GROUP_CODE_EDITOR_LABELS.deleteTitle}
            deleteDescription={GROUP_CODE_EDITOR_LABELS.deleteDescription}
            viewMode
            extraContent={extraContent}
            readExtraContent={readExtraContent}
        />
    );

    const showFieldPanel = showBatchNumber && groupFields != null && setFieldFlag;

    useEffect(() => {
        setRightPanelView("fieldSettings");
    }, [code?.id]);

    const childCodeEditor = (
        <CodeOptionTreeEditor
            parentId={code.id}
            options={code.children ?? []}
            onCreate={onCreateChild}
            onUpdate={onUpdateChild}
            onDelete={onDeleteChild}
            labels={GROUP_CODE_CHILD_LABELS}
            hideHeader={showFieldPanel}
        />
    );

    const fieldSettingsEditor = showFieldPanel ? (
        <FieldSet
            code={code}
            fields={groupFields}
            setFlag={setFieldFlag}
            managed
            embedded
            hideHeader
        />
    ) : null;

    const renderRightPanel = () => {
        if (!showFieldPanel) {
            return childCodeEditor;
        }

        return (
            <div className="group-code-right-panel">
                <div className="group-code-right-panel__head code-editor__meta">
                    <PanelTextNav
                        ariaLabel="자산그룹 설정"
                        options={ASSET_GROUP_RIGHT_PANEL_NAV}
                        value={rightPanelView}
                        onChange={setRightPanelView}
                    />
                </div>
                <div
                    className={`group-code-right-panel__body${
                        rightPanelView === "fieldSettings" ? " group-code-right-panel__body--field-settings" : ""
                    }`}
                >
                    {rightPanelView === "fieldSettings" ? fieldSettingsEditor : childCodeEditor}
                </div>
            </div>
        );
    };

    if (!supportsChildCodes) {
        if (showFieldPanel) {
            return (
                <div className="common-code-editor">
                    <div className="common-code-editor__info">
                        {editorPanel}
                    </div>
                    <div className="common-code-editor__options">
                        {fieldSettingsEditor}
                    </div>
                </div>
            );
        }

        return (
            <div className="common-code-editor common-code-editor--single">
                <div className="common-code-editor__info">
                    {editorPanel}
                </div>
            </div>
        );
    }

    return (
        <div className="common-code-editor">
            <div className="common-code-editor__info">
                {editorPanel}
            </div>
            <div className="common-code-editor__options">
                {renderRightPanel()}
            </div>
        </div>
    );
}

export default GroupCodeAssetEditor;
